import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, take } from "rxjs/operators";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { Address } from "src/app/lib/location/classes";
import { LocationService } from "src/app/lib/location/services/location.service";
import { IConcurrencyProviderService, IOfferProviderService } from "src/app/lib/shared/interfaces";
import { Offer, OfferItem } from "../../../classes";
import { OfferService } from "../../../services/offer.service";

@Injectable()
export class OfferDetailsComponentService implements IConcurrencyProviderService, IOfferProviderService {

    private _checkingVersion = new BehaviorSubject<boolean>(false);
    checkingVersion$: Observable<boolean> = this._checkingVersion.asObservable();

    private _offerId = new ReplaySubject<number>(1);
    offerId$: Observable<number> = this._offerId.asObservable();

    private _offer = new ReplaySubject<Offer>(1);
    offer$: Observable<Offer> = this._offer.asObservable();
    entityIdentifier$: Observable<number> = this._offer.pipe(map(x => x.offerid));

    concurrencyStamp$: Observable<string> = this._offer.pipe(map(x => x.concurrencystamp));
    validStamp$: Observable<boolean> = of(false);

    private _loading = new Subject<boolean>();
    loading$ = this._loading.asObservable();

    private _currentOfferSub: Subscription = null;
    private _subscriptions: Subscription[] = [];

    constructor(
        private _offerService: OfferService,
        private _locationService: LocationService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.setUp();
    }

    deleteCurrentOffer(): Observable<Object> {
        let subject = new Subject();
        this.offerId$.pipe(take(1), switchMap((offerId: number) => this._offerService.deleteOffer(offerId)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    dispose() {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentOffer();
    }

    refreshCurrentOffer(): Observable<Offer> {
        let subject = new Subject<Offer>();
        this._offerId.pipe(
            take(1),
            switchMap(x => this._offerService.getOfferById(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    saveChanges(offer: Offer = null, note: string = null, billingAddress: Address = null, serviceAddress: Address = null, offerItems: OfferItem[] = [], deleteOfferItems: number[] = []): Observable<Offer> {
        return combineLatest([
            billingAddress ? this._locationService[billingAddress.addressid ? 'updateAddress' : 'createAddress'](billingAddress) : of(null),
            serviceAddress ? this._locationService[serviceAddress.addressid ? 'updateAddress' : 'createAddress'](serviceAddress) : of(null)
        ]).pipe(switchMap(([billingAddress, serviceAddress]) => {
            offer.offerbillingaddress = billingAddress ? (billingAddress as Address).addressid : null;
            offer.offerserviceaddress = serviceAddress ? (serviceAddress as Address).addressid : null;
            return this._offerService.updateOffer(offer, note).pipe(
                switchMap((offer: Offer) => {
                    if(offerItems.length === 0) return of(offer);
                    offerItems.forEach(x => x.itemoffer = offer.offerid);
                    return combineLatest(offerItems.map((item: OfferItem) => this._offerService[item.itemid? 'updateOfferItem': 'createOfferItem'](item)))
                        .pipe(map(x => offer));
                }),
                switchMap((offer: Offer) => {
                    return deleteOfferItems.length === 0? of(offer): combineLatest(deleteOfferItems.map((itemId: number) => this._offerService.deleteOfferItem(itemId)))
                        .pipe(map(x => offer));
                })
            );
        }));
    }

    setOfferId(offerId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentOffer();
        this._currentOfferSub = this._offerService.getOfferById(offerId, false).subscribe({
            next: (offer: Offer) => {
                this._offer.next(offer);
                this._loading.next(false);
            },
            error: (error: HttpErrorResponse) => {
                switch(error.status){
                    case 404:
                        this._dialog.open(ErrorDialogComponent, {
                            panelClass: 'no-padding-dialog-container',
                            disableClose: true,
                            data: {
                                'description': `Es konnte kein Angebot mit der ID ${offerId} gefunden werden. Ist der Link eventuell veraltet oder wurde das Angebot in der Zwischenzeit gelöscht?`,
                                'error': error,
                                'showNavigateToMainPage': true
                            } as ErrorWrapper
                        });
                        break;
                }
                this._loading.next(false);
            }
        });
    }

    setUp(): void {
        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((offerId: number) => {
                console.log(offerId);
                this._offerId.next(offerId);
            }),
            this._offerId.pipe(distinctUntilChanged()).subscribe((identifier) => this.setOfferId(identifier))
        ]);
    }

    private _unsubscribeCurrentOffer() {
        if (this._currentOfferSub) {
            this._currentOfferSub.unsubscribe();
            this._currentOfferSub = null;
        }
    }

}